<script setup lang="ts">
import { computed, onMounted } from 'vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import { getColorFromScore } from '@/utils'
import Loading from '@/components/Loading.vue'
import { statsModule } from '@/store/stats/module'

const currentStudentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const currentAggStats = computed(() => statsModule.getters.getCurrentStudentAggStats())

const calcSubjectPercent = (subject: { name: string; correctCount: number; answeredCount: number }) => {
    return Math.round((subject.correctCount / subject.answeredCount) * 100)
}

const sortedInsights = computed(() => {
    const stats = currentAggStats.value
    return [ ...stats.subjectInsights.sort((a, b) => calcSubjectPercent(a) - calcSubjectPercent(b)) ]
})

onMounted(async () => {
    await Promise.all([
        statsModule.actions.fetchCurrentStudentStats(),
        statsModule.actions.fetchCurrentGroupAggStats(),
    ])
})
</script>

<template>
    <div class="subject-insights" :class="{ 'subject-insights--loading': currentStudentStats.isLoading }">
        <SectionHead
            title="Subject Insights"
            icon="subject"
            class="subject-insights__head"
        >
            <div v-if="currentAggStats.subjectInsights.length" class="subject-insights__head-ratio-label">
                Correct / Answered
            </div>
            <div v-if="currentAggStats.subjectInsights.length" class="subject-insights__head-score-label">
                Subject Score
            </div>
        </SectionHead>
        <Loading v-if="currentStudentStats.isLoading" />
        <ul v-else-if="currentAggStats.subjectInsights.length">
            <li
                v-for="subject in sortedInsights"
                :key="subject.name"
                :class="getColorFromScore(calcSubjectPercent(subject))"
            >
                <div class="subject-insights__subject-name">
                    {{ subject.name }}
                </div>
                <div class="subject-insights__subject-ratio">
                    {{ subject.correctCount }}
                    <span>/</span>
                    {{ subject.answeredCount }}
                </div>
                <div class="subject-insights__subject-score">
                    <div class="subject-insights__subject-score-percent">
                        {{ calcSubjectPercent(subject) }}<span>%</span>
                    </div>
                    <div class="subject-insights__subject-score-bar-container">
                        <div
                            class="subject-insights__subject-score-bar"
                            :style="{ width: calcSubjectPercent(subject) + '%' }"
                        />
                    </div>
                </div>
            </li>
        </ul>
        <p v-else>
            No subject insights.
        </p>
    </div>
</template>

<style lang="scss" scoped>


.subject-insights {
    .subject-insights__head {
        justify-content: start;
        align-items: flex-end;

        .subject-insights__head-ratio-label,
        .subject-insights__head-score-label {
            font-size: 11px;
        }

        .subject-insights__head-ratio-label {
            flex: 1;
            text-align: right;
            margin-right: 19px;
        }

        .subject-insights__head-score-label {
            flex: 2;
        }
    }

    ul {
        padding: 0;
        margin: 0;
        list-style-type: none;

        li {
            padding: 11px 0 7px;
            margin: 0;
            color: $slate-03;
            display: flex;
            width: 100%;
            position: relative;
            align-items: center;
            border-bottom: 1px solid $gray-divider;

            &::before {
                content: '';
                width: calc(100% + 28px);
                height: 100%;
                z-index: -1;
                position: absolute;
                left: -14px;
                top: 0;
                display: none;
            }

            &:hover::before {
                display: block;
            }

            &.red {
                &::before {
                    background: $red-hint;
                }

                &:hover .subject-insights__subject-score .subject-insights__subject-score-bar-container {
                    background: $rose-pink;
                }

                .subject-insights__subject-score-bar {
                    background: $brand-red;
                }
            }

            &.yellow {
                &::before {
                    background: $cream-puff;
                }

                &:hover .subject-insights__subject-score .subject-insights__subject-score-bar-container {
                    background: $beach-sand;
                }

                .subject-insights__subject-score-bar {
                    background: $butterscotch;
                }
            }

            &.green {
                &::before {
                    background: $green-hint;
                }

                &:hover .subject-insights__subject-score .subject-insights__subject-score-bar-container {
                    background: $meadow;
                }

                .subject-insights__subject-score-bar {
                    background: $green;
                }
            }

            .subject-insights__subject-name {
                font-size: 14px;
                line-height: 19px;
                flex: 2;
            }

            .subject-insights__subject-ratio {
                flex: 1;
                font-weight: 400;
                text-align: right;
                margin-right: 19px;
                font-size: 16px;
                letter-spacing: -0.3px;

                span {
                    color: $pewter;
                }
            }

            .subject-insights__subject-score {
                flex: 2;
                display: flex;
                align-items: center;

                .subject-insights__subject-score-percent {
                    font-weight: 600;
                    width: 50px;
                    font-size: 18px;
                    margin-right: 10px;
                    letter-spacing: -0.3px;
                    text-align: right;

                    span {
                        margin-left: 2px;
                        font-weight: 400;
                    }
                }

                .subject-insights__subject-score-bar-container {
                    background: $gray-progress-background;
                    flex: 1;
                    border-radius: 2px;
                    overflow: hidden;
                    height: 13.5px;

                    .subject-insights__subject-score-bar {
                        border-radius: 2px;
                        height: 13.5px;
                    }
                }
            }
        }
    }

    &--loading {
        ul li {
            filter: blur(4px);
        }
    }
}
</style>
